// src/components/sla/ai/SlaDecisionExplainer.js
// ─────────────────────────────────────────────────────────────────────────────
// Drawer that explains one SLA AI decision:
//   What FlowSense AI did · Why (reasons) · how the outgoing assignee was graded
//   (factor bars) · the ranked candidates with their factor scores · who was notified
//   · the review trail — plus supervisor actions: approve (pick a candidate), reject,
//   revert an executed reassignment, each with an optional note.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerFooter, DrawerHeader, DrawerOverlay, Flex, HStack, Icon, List, ListIcon, ListItem, Radio, RadioGroup, SimpleGrid,
    Skeleton, Stack, Table, Tbody, Td, Text, Textarea, Th, Thead, Tooltip, Tr, VStack, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdChevronRight, MdEmail, MdNotificationsActive, MdPerson, MdRefresh } from 'react-icons/md';
import slaAiApi from '../../../services/slaAiApi';
import { useSlaAiActions } from '../../../hooks/useSlaAi';
import SlaGradeBadge from './SlaGradeBadge';
import SlaFactorBars from './SlaFactorBars';
import {
    ACTION_META, GRADE_FACTOR_LABELS, RANK_FACTOR_LABELS, STATUS_META, fmtDateTime, fmtMinutes, scoreScheme,
} from './slaAiFormat';

function Section({ title, icon, children, right }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Box>
            <Flex align="center" justify="space-between" mb={2}>
                <HStack spacing={2}>
                    {icon && <Icon as={icon} color="purple.400" />}
                    <Text fontSize="xs" fontWeight="800" letterSpacing="wider" textTransform="uppercase" color={muted}>{title}</Text>
                </HStack>
                {right}
            </Flex>
            {children}
        </Box>
    );
}

export default function SlaDecisionExplainer({ decisionId, decision: initial = null, isOpen, onClose, onChanged }) {
    const [decision, setDecision] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [note, setNote] = useState('');
    const [pick, setPick] = useState('');
    const actions = useSlaAiActions();
    const toast = useToast();
    const mounted = useRef(true);

    const cardBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const cardBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const rowBest = useColorModeValue('purple.50', 'whiteAlpha.100');

    const id = decisionId || initial?.id;
    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const d = await slaAiApi.decision(id);
            if (mounted.current) { setDecision(d); setPick(d.new_assignee_id || d.candidates?.[0]?.id || ''); }
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [id]);

    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (!isOpen) return;
        setNote('');
        if (initial && initial.id === id) { setDecision(initial); setPick(initial.new_assignee_id || initial.candidates?.[0]?.id || ''); }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, id]);

    const d = decision;
    const meta = ACTION_META[d?.decision_type] || ACTION_META.escalate;
    const status = STATUS_META[d?.status] || STATUS_META.executed;
    const canApprove = d && (d.status === 'pending_approval' || (d.decision_type === 'escalate' && d.status === 'executed' && d.candidates?.length > 0));
    const canReject = d && (d.status === 'pending_approval' || (d.decision_type === 'escalate' && d.status === 'executed'));
    const canRevert = d && ['reassign', 'recommend'].includes(d.decision_type) && ['executed', 'approved'].includes(d.status) && d.previous_assignee_id;
    const gradeFactors = d?.factors?.grade || null;
    const email = d?.notified?.email || {};

    const act = useCallback(async (kind) => {
        try {
            let res;
            if (kind === 'approve') res = await actions.approve(d.id, { userId: pick || undefined, note });
            else if (kind === 'reject') res = await actions.reject(d.id, { note });
            else res = await actions.revert(d.id, { note });
            toast({ title: res.message || 'Saved', status: 'success', duration: 4000, isClosable: true });
            if (res.data && mounted.current) setDecision(res.data);
            onChanged?.(res.data);
        } catch (e) {
            toast({ title: 'Action failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
        }
    }, [actions, d, pick, note, toast, onChanged]);

    const candidates = useMemo(() => d?.candidates || [], [d]);

    return (
        <Drawer isOpen={isOpen} onClose={onClose} size="lg" placement="right">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">
                    <HStack spacing={3} align="start">
                        <Flex w="40px" h="40px" borderRadius="xl" bg={`${meta.scheme}.500`} color="white" align="center" justify="center"><Icon as={meta.icon} boxSize={5} /></Flex>
                        <Box flex="1" minW={0}>
                            <Text fontSize="lg" fontWeight="800">{meta.label}</Text>
                            <HStack spacing={2} mt={1} flexWrap="wrap">
                                <Badge colorScheme={status.scheme}>{status.label}</Badge>
                                {d?.escalation_level ? <Badge colorScheme="red" variant="subtle">Level {d.escalation_level}</Badge> : null}
                                {d?.trigger_source && d.trigger_source !== 'auto' && <Badge variant="outline">{d.trigger_source}</Badge>}
                                {d && <Text fontSize="xs" color={muted}>WO #{d.work_order_id} · {fmtDateTime(d.created_at)}</Text>}
                            </HStack>
                        </Box>
                    </HStack>
                </DrawerHeader>

                <DrawerBody py={5}>
                    {error && (
                        <Alert status="error" borderRadius="lg" mb={4}>
                            <AlertIcon /><AlertDescription flex="1">{error}</AlertDescription>
                            <Button size="xs" leftIcon={<MdRefresh />} onClick={load}>Retry</Button>
                        </Alert>
                    )}
                    {!d && loading && <Stack spacing={3}><Skeleton h="80px" borderRadius="lg" /><Skeleton h="140px" borderRadius="lg" /><Skeleton h="180px" borderRadius="lg" /></Stack>}
                    {d && (
                        <VStack align="stretch" spacing={6}>
                            <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
                                <HStack mb={2}><Icon as={MdAutoAwesome} color="purple.500" /><Text fontWeight="800">What FlowSense AI did</Text></HStack>
                                <Text>{d.summary}</Text>
                                <HStack mt={3} spacing={2} flexWrap="wrap">
                                    <Badge colorScheme="purple" variant="subtle">{d.model_version}</Badge>
                                    <Tooltip label={d.engine === 'ml' ? 'Decided by the Python ML service' : 'ML service was unavailable — the identical Node mirror decided'} hasArrow>
                                        <Badge variant="outline">{d.engine === 'ml' ? 'ML service' : 'Node mirror'}</Badge>
                                    </Tooltip>
                                    {d.confidence != null && <Badge colorScheme={d.confidence >= 0.75 ? 'green' : d.confidence >= 0.6 ? 'yellow' : 'orange'}>{Math.round(d.confidence * 100)}% confidence</Badge>}
                                    {d.minutes_late ? <Badge colorScheme="red">{fmtMinutes(d.minutes_late)} late</Badge> : null}
                                </HStack>
                            </Box>

                            <Section title="Why" icon={MdChevronRight}>
                                <List spacing={2}>
                                    {(d.reasons || []).map((r, i) => (
                                        <ListItem key={i} display="flex" alignItems="flex-start"><ListIcon as={MdChevronRight} color="purple.400" mt="3px" /><Text fontSize="sm">{r}</Text></ListItem>
                                    ))}
                                </List>
                            </Section>

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <Section title="From" icon={MdPerson}>
                                    <HStack>
                                        <SlaGradeBadge grade={d.grade_letter} score={d.grade_score} projected />
                                        <Box><Text fontWeight="700">{d.previous_assignee_name || 'Unassigned'}</Text><Text fontSize="xs" color={muted}>{d.assignee_type || 'tech'} · graded at decision time</Text></Box>
                                    </HStack>
                                </Section>
                                <Section title={d.decision_type === 'recommend' && d.status === 'pending_approval' ? 'Recommended' : 'To'} icon={MdPerson}>
                                    {d.new_assignee_name
                                        ? <Box><Text fontWeight="700">{d.new_assignee_name}</Text><Text fontSize="xs" color={muted}>{candidates[0]?.reasons?.slice(0, 2).join(' ') || '—'}</Text></Box>
                                        : <Text fontSize="sm" color={muted}>{d.decision_type === 'warning' ? 'No change — warning only.' : 'Stays with the current assignee.'}</Text>}
                                </Section>
                            </SimpleGrid>

                            {gradeFactors && (
                                <Section title="How the work was graded">
                                    <SlaFactorBars factors={gradeFactors} labels={GRADE_FACTOR_LABELS} weights={{ timeliness: 0.45, response: 0.15, quality: 0.25, compliance: 0.15 }} />
                                    {d.factors?.quality_source === 'default' && <Text fontSize="xs" color={muted} mt={2}>No QA audit or checklist yet — quality uses the neutral default (75).</Text>}
                                </Section>
                            )}

                            {candidates.length > 0 && (
                                <Section title={`Candidates ranked (${candidates.length})`}>
                                    <RadioGroup value={pick} onChange={setPick} isDisabled={!canApprove}>
                                        <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
                                            <Table size="sm">
                                                <Thead><Tr>{canApprove && <Th w="1%" />}<Th>#</Th><Th>Who</Th><Th isNumeric>Score</Th>{Object.keys(RANK_FACTOR_LABELS).map(k => <Th key={k} isNumeric display={{ base: 'none', md: 'table-cell' }}>{RANK_FACTOR_LABELS[k].split(' ')[0]}</Th>)}</Tr></Thead>
                                                <Tbody>
                                                    {candidates.map((c, i) => (
                                                        <Tr key={c.id} bg={i === 0 ? rowBest : undefined}>
                                                            {canApprove && <Td><Radio value={String(c.id)} aria-label={`Assign ${c.name}`} /></Td>}
                                                            <Td>{i + 1}</Td>
                                                            <Td>
                                                                <Tooltip label={(c.reasons || []).join(' ')} hasArrow placement="top-start">
                                                                    <Box><Text fontWeight="700" fontSize="sm">{c.name}</Text><Text fontSize="2xs" color={muted}>{c.type}{c.distance_km != null ? ` · ${c.distance_km} km` : ''} · {c.open_jobs} open</Text></Box>
                                                                </Tooltip>
                                                            </Td>
                                                            <Td isNumeric><Badge colorScheme={scoreScheme(c.score)}>{c.score}</Badge></Td>
                                                            {Object.keys(RANK_FACTOR_LABELS).map(k => <Td key={k} isNumeric fontSize="xs" display={{ base: 'none', md: 'table-cell' }}>{c.factors?.[k] ?? '—'}</Td>)}
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </RadioGroup>
                                </Section>
                            )}

                            <Section title="Who was told" icon={MdNotificationsActive}>
                                <HStack spacing={4} flexWrap="wrap" fontSize="sm">
                                    <HStack><Icon as={MdNotificationsActive} color="purple.400" /><Text>{d.notified?.in_app ?? 0} in-app</Text></HStack>
                                    <HStack><Icon as={MdEmail} color="blue.400" /><Text>{email.sent?.length ?? 0} email{(email.sent?.length ?? 0) === 1 ? '' : 's'}{email.failed?.length ? ` · ${email.failed.length} failed` : ''}{email.skipped ? ` · ${email.skipped}` : ''}</Text></HStack>
                                    {d.notified?.push ? <Text>{d.notified.push} push</Text> : null}
                                </HStack>
                                {email.sent?.length > 0 && <Text fontSize="xs" color={muted} mt={1}>{email.sent.join(', ')}</Text>}
                            </Section>

                            {(d.reviewed_at || d.review_note) && (
                                <Section title="Review">
                                    <Text fontSize="sm">{status.label} {d.reviewed_at ? `on ${fmtDateTime(d.reviewed_at)}` : ''}{d.reviewed_by_user_id ? ` by user #${d.reviewed_by_user_id}` : ''}</Text>
                                    {d.review_note && <Text fontSize="sm" color={muted} mt={1}>“{d.review_note}”</Text>}
                                </Section>
                            )}

                            {(canApprove || canReject || canRevert) && (
                                <>
                                    <Divider />
                                    <Textarea placeholder="Note for the decision log (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} size="sm" borderRadius="lg" />
                                </>
                            )}
                        </VStack>
                    )}
                </DrawerBody>

                {d && (canApprove || canReject || canRevert) && (
                    <DrawerFooter borderTopWidth="1px" gap={2} flexWrap="wrap">
                        {canRevert && <Button variant="outline" colorScheme="orange" onClick={() => act('revert')} isLoading={actions.busy === `revert:${d.id}`}>Revert reassignment</Button>}
                        {canReject && <Button variant="ghost" colorScheme="red" onClick={() => act('reject')} isLoading={actions.busy === `reject:${d.id}`}>Reject</Button>}
                        {canApprove && (
                            <Button colorScheme="purple" onClick={() => act('approve')} isDisabled={!pick} isLoading={actions.busy === `approve:${d.id}`}>
                                Approve · assign {candidates.find(c => String(c.id) === String(pick))?.name || ''}
                            </Button>
                        )}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
}
